import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Table } from '@/common';
import { Spinner } from '@/components/generic';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { PAGE_SIZE } from '@/constants';
import { PERMISSION_TYPE } from '@/enums';
import { getPlayerReleaseColumns } from '@/helpers/PlayerReleasesHelper';
import {
  useDeletePlayerReleaseMutation,
  useGetAllPlayerReleases
} from '@/services/playerRelease.service';
import { AllowedPermissions, TableData } from '@/types';
import { PlayerRelease } from '@/types/PlayerReleaseTypes';

export const PlayerReleases = () => {
  const [t] = useTranslation('common');
  const navigate = useNavigate();
  const [selectedPlayerRelease, setSelectedPlayerRelease] = useState<PlayerRelease | undefined>();

  const permissions: AllowedPermissions = {
    createPermission: PERMISSION_TYPE.CREATE_PLAYER_RELEASE,
    editPermission: PERMISSION_TYPE.UPDATE_PLAYER_RELEASE,
    deletePermission: PERMISSION_TYPE.DELETE_PLAYER_RELEASE,
    viewPermission: PERMISSION_TYPE.VIEW_PLAYER_RELEASES
  };

  const { mutate: deletePlayerRelease } = useDeletePlayerReleaseMutation(queryClient, {
    onSuccess: () => {
      setSelectedPlayerRelease(undefined);
    },
    onError: () => {
      setSelectedPlayerRelease(undefined);
    }
  });

  const handleViewDetails = (row: TableData) => {
    navigate('/player-releases/' + row.id);
  };

  const {
    isLoading,
    data: allPlayerReleases,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useGetAllPlayerReleases(PAGE_SIZE);

  const formatData = (): TableData[] => {
    return allPlayerReleases?.pages.flatMap((page) =>
      page.data?.items.map((playerReleaseData: PlayerRelease) => ({
        ...playerReleaseData,
        enabled: true
      }))
    ) as TableData[];
  };

  return (
    <div className='flex size-full flex-col items-center'>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className='flex h-screen w-full flex-col px-3 pb-3'>
          {allPlayerReleases && (
            <Table
              data={formatData()}
              columns={getPlayerReleaseColumns()}
              tableTitle={t('playerReleases')}
              addButtonLabel={t('addPlayerRelease')}
              permissions={permissions}
              handleCreate={() => navigate('/player-releases/create')}
              handleEdit={(item) => navigate(`/player-releases/${item.id}/edit`)}
              handleDelete={(item) => setSelectedPlayerRelease(item as PlayerRelease)}
              handleViewDetails={(item) => handleViewDetails(item)}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              widerLayout
              numberOfAppliedFilters={0}
              hiddenColumnsMobile={['status']}
            />
          )}
          {selectedPlayerRelease && (
            <ConfirmationModal
              isOpen={!!selectedPlayerRelease}
              message={t('deleteMessage', { item: selectedPlayerRelease.version })}
              onClose={() => setSelectedPlayerRelease(undefined)}
              onConfirm={() => deletePlayerRelease(selectedPlayerRelease?.id)}
            />
          )}
        </div>
      )}
    </div>
  );
};
